import { useState, useEffect } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Bell, Plus, Trash2, AlertTriangle, Check, X, Hash, Activity } from "lucide-react";
import { toast } from "sonner";

const statusColors = { ok: { bg: "rgba(45,106,79,0.12)", color: "#2D6A4F" }, warn: { bg: "rgba(231,111,81,0.12)", color: "#E76F51" }, alert: { bg: "rgba(190,52,85,0.12)", color: "#BE3455" } };
const sevColors = { critical: "#EF4444", high: "#F59E0B", medium: "#EAB308", low: "#29B5E8" };

export default function MonitorsPage() {
  const { user } = useAuth();
  const [monitors, setMonitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", type: "metric", pipeline_id: "", metric: "freshness_lag", operator: ">", threshold: 0, severity: "high", notify_channels: [], message: "" });
  const [channelInput, setChannelInput] = useState("");
  const isAdmin = user?.role === "admin";

  const fetch = async () => { try { const { data } = await api.get("/monitors"); setMonitors(data); } catch (err) { console.error(err); } finally { setLoading(false); } };
  useEffect(() => { fetch(); }, []);

  const handleCreate = async () => {
    try { await api.post("/monitors", form); fetch(); setShowCreate(false); toast.success("Monitor created"); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const handleToggle = async (id) => { try { await api.patch(`/monitors/${id}/toggle`); fetch(); } catch (err) { console.error(err); } };
  const handleDelete = async (id) => { try { await api.delete(`/monitors/${id}`); fetch(); toast.success("Deleted"); } catch (err) { console.error(err); } };

  const alerting = monitors.filter((m) => m.status === "alert").length;
  const warning = monitors.filter((m) => m.status === "warn").length;

  if (loading) return <div className="p-6" data-testid="monitors-loading"><Skeleton className="h-8 w-48 bg-white/5" /></div>;

  return (
    <div className="p-6 space-y-6" data-testid="monitors-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[26px] font-extrabold tracking-tight text-[#2D3142]">Monitors</h1>
          <p className="text-[13px] mt-0.5" style={{ color: "#6B7280" }}>{monitors.length} monitors / {alerting} alerting / {warning} warning</p>
        </div>
        {isAdmin && (
          <Button className="rounded-lg font-semibold text-sm border-0 text-[#2D3142]" style={{ background: "linear-gradient(135deg, #29B5E8 0%, #1B8FC2 100%)" }} onClick={() => setShowCreate(true)} data-testid="create-monitor-button">
            <Plus className="w-4 h-4 mr-2" /> New Monitor
          </Button>
        )}
      </div>

      {/* Status summary */}
      <div className="flex gap-3">
        {[{ label: "OK", count: monitors.filter((m) => m.status === "ok").length, s: "ok" }, { label: "Warning", count: warning, s: "warn" }, { label: "Alerting", count: alerting, s: "alert" }].map((item) => (
          <div key={item.label} className="sf-card px-4 py-3 flex items-center gap-3" data-testid={`monitor-status-${item.s}`}>
            <div className="w-3 h-3 rounded-full" style={{ background: statusColors[item.s].color, boxShadow: `0 0 8px ${statusColors[item.s].color}40` }} />
            <span className="text-[13px] font-medium text-[#2D3142]">{item.count}</span>
            <span className="text-[11px]" style={{ color: "#6B7280" }}>{item.label}</span>
          </div>
        ))}
      </div>

      {/* Monitor list */}
      <div className="space-y-2" data-testid="monitors-list">
        {monitors.map((m, i) => {
          const sc = statusColors[m.status] || statusColors.ok;
          return (
            <div key={m.monitor_id} className="sf-card p-4 animate-fade-up" style={{ animationDelay: `${i * 40}ms` }} data-testid={`monitor-${m.monitor_id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1">
                  <div className="w-2.5 h-10 rounded-full" style={{ background: sc.color }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[14px] font-semibold text-[#2D3142]">{m.name}</span>
                      <Badge className="text-[8px] uppercase font-bold border-0 rounded-md" style={{ background: sc.bg, color: sc.color }}>{m.status}</Badge>
                      <Badge className="text-[8px] uppercase font-bold border-0 rounded-md" style={{ background: `${sevColors[m.severity]}15`, color: sevColors[m.severity] }}>{m.severity}</Badge>
                    </div>
                    <div className="flex items-center gap-4 text-[11px]" style={{ color: "#6B7280" }}>
                      <span className="font-mono">{m.metric} {m.operator} {m.threshold}</span>
                      {m.pipeline_id && <span className="font-mono">{m.pipeline_id}</span>}
                      <span>{m.trigger_count} triggers</span>
                      {m.notify_channels?.length > 0 && (
                        <span className="flex items-center gap-1"><Hash className="w-3 h-3" />{m.notify_channels.join(", ")}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {m.last_triggered && <span className="text-[10px] font-mono" style={{ color: "#9CA3AF" }}>Last: {m.last_triggered?.slice(0, 16).replace("T", " ")}</span>}
                  {isAdmin && (
                    <>
                      <Switch checked={m.is_active} onCheckedChange={() => handleToggle(m.monitor_id)} data-testid={`toggle-${m.monitor_id}`} />
                      <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg text-[#5A6B82] hover:text-[#EF4444] hover:bg-[#EF4444]/10" onClick={() => handleDelete(m.monitor_id)} data-testid={`delete-${m.monitor_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-[480px]" style={{ background: "#FFFFFF", border: "1px solid #E8E4DF" }} data-testid="create-monitor-dialog">
          <DialogHeader><DialogTitle className="text-[#2D3142]">Create Monitor</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5"><Label className="text-[11px] font-semibold" style={{ color: "#6B7280" }}>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Monitor name" className="h-9 rounded-lg bg-black/[0.02] border-gray-200 text-[#2D3142]" data-testid="monitor-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-[11px] font-semibold" style={{ color: "#6B7280" }}>Metric</Label>
                <Select value={form.metric} onValueChange={(v) => setForm({ ...form, metric: v })}>
                  <SelectTrigger className="h-9 rounded-lg bg-black/[0.02] border-gray-200 text-[#2D3142]"><SelectValue /></SelectTrigger>
                  <SelectContent style={{ background: "#FFFFFF" }}><SelectItem value="freshness_lag">Freshness Lag</SelectItem><SelectItem value="row_count_delta">Row Count Delta</SelectItem><SelectItem value="null_rate">Null Rate</SelectItem><SelectItem value="duplicate_rate">Duplicate Rate</SelectItem><SelectItem value="consecutive_failures">Consecutive Failures</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label className="text-[11px] font-semibold" style={{ color: "#6B7280" }}>Threshold</Label>
                <div className="flex gap-1">
                  <Select value={form.operator} onValueChange={(v) => setForm({ ...form, operator: v })}>
                    <SelectTrigger className="w-16 h-9 rounded-lg bg-black/[0.02] border-gray-200 text-[#2D3142]"><SelectValue /></SelectTrigger>
                    <SelectContent style={{ background: "#FFFFFF" }}><SelectItem value=">">{">"}</SelectItem><SelectItem value="<">{"<"}</SelectItem><SelectItem value=">=">{">="}</SelectItem></SelectContent>
                  </Select>
                  <Input type="number" value={form.threshold} onChange={(e) => setForm({ ...form, threshold: parseFloat(e.target.value) || 0 })} className="h-9 rounded-lg bg-black/[0.02] border-gray-200 text-[#2D3142]" />
                </div>
              </div>
            </div>
            <div className="space-y-1.5"><Label className="text-[11px] font-semibold" style={{ color: "#6B7280" }}>Severity</Label>
              <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                <SelectTrigger className="h-9 rounded-lg bg-black/[0.02] border-gray-200 text-[#2D3142]"><SelectValue /></SelectTrigger>
                <SelectContent style={{ background: "#FFFFFF" }}><SelectItem value="critical">Critical</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="low">Low</SelectItem></SelectContent>
              </Select>
            </div>
            <DialogFooter><Button className="rounded-lg font-semibold text-sm border-0 text-[#2D3142]" style={{ background: "linear-gradient(135deg, #29B5E8 0%, #1B8FC2 100%)" }} onClick={handleCreate} disabled={!form.name} data-testid="submit-monitor-button">Create Monitor</Button></DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
